/* Client-side mirror of the world. Terrain is generated locally from the shared seed (no terrain is ever
   downloaded); the server only sends which pixels have been dug (per-chunk bitmaps) and the excavation
   graph, which the client replays to carve new tunnels as they happen. */
import { W, CH, SURF, LAYERS, ORE_BY_ID, ORE_BASE, MAT_CAVE, MAT_GEODE, MAT_FOSSIL, MAT_RELIC, MAT_METEOR, MAT_AQUIFER, M_PER_PX, layerAtM } from '../shared/config';
import { Terrain } from '../shared/terrain';
import { hash2, vnoise } from '../shared/rng';
import { GNode, segPts, carveSteps, carveEllipse, carveDisc, NodeRec } from '../shared/graph';

export type RGB = [number, number, number];
export const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
export const mix = (a: RGB, b: RGB, k: number): RGB => [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
export const shade = (c: RGB, d: number): RGB => [c[0] + d, c[1] + d, c[2] + d];
export const css = (c: RGB, a = 1) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

const LAYER_RGB = LAYERS.map(l => ({ a: hex(l.c[0]), b: hex(l.c[1]), acc: hex(l.accent) }));
const TUN = hex('#1c1b1c'), FLOOR = hex('#3a3834');
const SKY_TOP = hex('#08090b'), SKY_LOW = hex('#1a1c21'), HAZE = hex('#2b2722'), RIDGE = hex('#121316'), RIDGE2 = hex('#16181c');

export interface CChunk { k: number; mat: Uint8Array; dug: Uint8Array; rgba: Uint8ClampedArray; ver: number; state: 'gen' | 'pending' | 'ready'; used: number }

export class World {
  terrain!: Terrain; seed = 0;
  chunks = new Map<number, CChunk>();
  nodes: GNode[] = [];
  heap = new Float32Array(W);
  ready = false;

  setSeed(seed: number) {
    if (this.terrain && this.seed === seed) return;
    this.seed = seed; this.terrain = new Terrain(seed); this.chunks.clear(); this.nodes = [];
  }
  /* generate rock colours for a chunk (dig state arrives separately) */
  chunk(k: number): CChunk {
    let c = this.chunks.get(k);
    if (!c) {
      const mat = this.terrain.genChunk(k);
      c = { k, mat, dug: new Uint8Array(W * CH), rgba: new Uint8ClampedArray(W * CH * 4), ver: -1, state: 'gen', used: performance.now() };
      this.chunks.set(k, c);
      for (let r = 0; r < CH; r++) for (let x = 0; x < W; x++) this.paint(c, x, r);
    }
    c.used = performance.now();
    return c;
  }
  has(k: number) { const c = this.chunks.get(k); return !!c && c.state === 'ready'; }
  dugAt(x: number, y: number) {
    if (x < 0 || x >= W || y < 0) return 1;
    const c = this.chunks.get(Math.floor(y / CH)); if (!c) return 0;
    return c.dug[(y % CH) * W + x];
  }
  matAt(x: number, y: number) { x = Math.round(x); y = Math.round(y); if (x < 0 || x >= W || y < 0) return 0; return this.chunk(Math.floor(y / CH)).mat[(y % CH) * W + x]; }

  applyChunk(k: number, ver: number, dug: Uint8Array) {
    const c = this.chunk(k);
    c.dug = dug; c.ver = ver; c.state = 'ready';
    for (let r = 0; r < CH; r++) for (let x = 0; x < W; x++) this.paint(c, x, r);
    /* neighbours' edge rows depend on this chunk's dig state */
    const up = this.chunks.get(k - 1), dn = this.chunks.get(k + 1);
    if (up) for (let x = 0; x < W; x++) this.paint(up, x, CH - 1);
    if (dn) for (let x = 0; x < W; x++) this.paint(dn, x, 0);
  }
  /* keep memory bounded: forget chunks far from view */
  evict(keepLo: number, keepHi: number, max = 48) {
    if (this.chunks.size <= max) return;
    const list = [...this.chunks.values()].filter(c => c.k < keepLo || c.k > keepHi).sort((a, b) => a.used - b.used);
    for (const c of list.slice(0, this.chunks.size - max)) this.chunks.delete(c.k);
  }
  invalidateDug() { for (const c of this.chunks.values()) if (c.state === 'ready') c.state = 'gen'; }

  /* carve a pixel the server reported dug (only into chunks we hold; others arrive fresh later) */
  dig = (x: number, y: number) => {
    if (x < 1 || x >= W - 1 || y < 0) return;
    const c = this.chunks.get(Math.floor(y / CH)); if (!c || c.state !== 'ready') return;
    if (y < this.terrain.surf[x]) return;
    const j = (y % CH) * W + x; if (c.dug[j]) return;
    c.dug[j] = 1;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) this.repaint(x + dx, y + dy);
  };
  private repaint(x: number, y: number) { if (x < 0 || x >= W || y < 0) return; const c = this.chunks.get(Math.floor(y / CH)); if (c) this.paint(c, x, y % CH); }

  /* ---------- pixel colour ---------- */
  paint(c: CChunk, x: number, r: number) {
    const y = c.k * CH + r, i = (r * W + x) * 4, rgb = this.colorAt(c, x, y, r);
    c.rgba[i] = rgb[0]; c.rgba[i + 1] = rgb[1]; c.rgba[i + 2] = rgb[2]; c.rgba[i + 3] = 255;
  }
  private colorAt(c: CChunk, x: number, y: number, r: number): RGB {
    const s = this.terrain.surf[x], seed = this.seed;
    if (y < s) return this.sky(x, y, s);
    const m = c.mat[r * W + x], dp = y - s, li = layerAtM(Math.max(0, dp) * M_PER_PX), L = LAYER_RGB[li];
    const h = hash2(x, y, seed + 5);
    if (c.dug[r * W + x]) {
      /* excavated: dark cavity, slightly lit floor where rock lies beneath */
      const below = this.dugAt(x, y + 1), above = this.dugAt(x, y - 1);
      let col = mix(L.a, TUN, .74);
      if (!below) col = mix(L.a, FLOOR, .55);
      else if (!above) col = mix(col, [0, 0, 0], .3);
      return shade(col, (h - .5) * 4);
    }
    let col = this.rock(m, li, x, y, h);
    /* tunnel wall rim: rock touching open space reads as a cut face */
    if (this.dugAt(x - 1, y) || this.dugAt(x + 1, y) || this.dugAt(x, y - 1) || this.dugAt(x, y + 1)) {
      col = this.dugAt(x, y - 1) ? shade(col, 18) : mix(col, [0, 0, 0], .38);
    }
    if (dp === 0) col = mix(col, hex('#4a4740'), .6);
    return col;
  }
  private sky(x: number, y: number, s: number): RGB {
    const t = y / SURF;
    let col = mix(SKY_TOP, SKY_LOW, Math.pow(t, 1.6));
    if (y > s - 16) col = mix(col, HAZE, (y - (s - 16)) / 16 * .55);
    const r1 = s - 22 - vnoise(x / 34, 1.5, this.seed + 91) * 26, r2 = s - 8 - vnoise(x / 15, 4.5, this.seed + 92) * 12;
    if (y >= r1) col = RIDGE;
    if (y >= r2) col = RIDGE2;
    if (y < r1 - 2) { const h = hash2(x, y, this.seed + 93); if (h < .0035) col = mix(col, [228, 222, 206], .35 + h * 150); }
    return col;
  }
  private rock(m: number, li: number, x: number, y: number, h: number): RGB {
    const L = LAYER_RGB[li], seed = this.seed;
    if (m >= ORE_BASE && m < 32) {
      const o = ORE_BY_ID[m]; if (!o) return L.a;
      return h < .18 ? mix(hex(o.hi), L.a, .2) : h < .7 ? mix(hex(o.col), L.a, .3) : mix(hex(o.col), L.a, .6);
    }
    switch (m) {
      case MAT_CAVE: return h < .55 ? mix(L.b, [0, 0, 0], .55) : h < .9 ? mix(L.a, [0, 0, 0], .42) : mix(L.acc, [0, 0, 0], .2);
      case MAT_GEODE: return h < .35 ? [159, 216, 210] : h < .6 ? [95, 150, 160] : [43, 53, 64];
      case MAT_FOSSIL: return h < .45 ? [207, 195, 163] : [74, 63, 48];
      case MAT_RELIC: return h < .3 ? [200, 145, 58] : h < .7 ? [94, 138, 122] : [40, 46, 44];
      case MAT_METEOR: return h < .3 ? [185, 188, 196] : [58, 58, 68];
      case MAT_AQUIFER: return h < .3 ? [79, 111, 122] : [36, 50, 58];
    }
    /* layered rock with strata banding, dither and per-layer texture */
    let col = h < .5 ? L.a : L.b;
    const band = (vnoise(x / 48, y / 5, seed + 31) - .5) * (li <= 3 || li === 7 ? 16 : 7);
    col = shade(col, band);
    const h2 = hash2(y, x + 7, seed + 6);
    if (h2 < .04) col = shade(col, 14); else if (h2 > .965) col = shade(col, -12);
    if (li === 4 && h2 > .5 && h2 < .53) col = mix(col, [190, 160, 160], .5);          // granite feldspar flecks
    if (li === 5 && h2 > .5 && h2 < .52) col = [22, 23, 26];                            // basalt vesicles
    if (li === 6 && h2 > .5 && h2 < .508) col = [120, 200, 205];                         // crystal glints
    if (li === 8 && h2 > .5 && h2 < .515) col = [210, 90, 50];                           // core embers
    if (li === 0 && h2 > .5 && h2 < .56) col = shade(col, 10);                           // sand grain
    return col;
  }

  /* ---------- excavation graph ---------- */
  applyNode(r: NodeRec) {
    const [id, x, y, parent, state, prog, carved, cp1000, goal, type, r100, flags, build1000, link, cargo] = r;
    let n = this.nodes[id];
    if (!n) {
      const p = parent >= 0 ? this.nodes[parent] : null;
      n = { id, x, y, parent, state, prog: 0, carved: 0, cp: 0, goal, type, r: r100 / 100, flags, build: 0, link, cargo: 0, pts: p ? segPts(p.x, p.y, x, y) : [[x, y]] };
      this.nodes[id] = n;
      if (parent < 0) carveDisc(x, y, 9, this.dig);
    }
    if (carved > n.carved) { carveSteps(n, n.carved, carved, this.dig); n.carved = carved; }
    const cp = cp1000 / 1000;
    if (cp > n.cp) { carveEllipse(n.x, n.y, n.r, cp, this.dig); n.cp = cp; }
    n.state = state; n.prog = prog; n.goal = goal; n.flags = flags; n.build = build1000 / 1000; n.link = link; n.cargo = cargo;
    return n;
  }
  /* after a chunk arrives, carving known to the graph is already in its bitmap; nothing to replay */
  deepestY() { let m = SURF; for (const n of this.nodes) if (n && n.y > m) m = n.y; return m; }
}
